import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Statistic, Table, Tag, Spin, message, Badge, Progress, Tooltip, Empty } from 'antd';
import { TeamOutlined, MailOutlined, EyeOutlined, CheckCircleOutlined, SyncOutlined, GlobalOutlined, RocketOutlined, BookOutlined, ShoppingOutlined, MessageOutlined, FireOutlined, WarningOutlined, UserAddOutlined, PauseCircleOutlined, UserOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { dashboardApi } from '../services/api';

const PLATFORM_CONFIG = {
  ALL:       { label: '전체',      color: '#1890ff', bg: '#e6f7ff', icon: <GlobalOutlined />,   emoji: '🌐' },
  REAL:      { label: 'REAL',      color: '#722ed1', bg: '#f9f0ff', icon: <BookOutlined />,     emoji: '📚' },
  TRADESAGA: { label: 'TradeSaga', color: '#f5222d', bg: '#fff1f0', icon: <RocketOutlined />,   emoji: '📈' },
  WORKON:    { label: 'WorkOn',    color: '#13c2c2', bg: '#e6fffb', icon: <ShoppingOutlined />, emoji: '💼' },
  TALK:      { label: 'Talk',      color: '#52c41a', bg: '#f6ffed', icon: <MessageOutlined />,  emoji: '💬' },
};

const USER_TYPE_CONFIG = {
  power_user: { label: '파워유저', icon: <FireOutlined />,        color: '#f5222d', bg: '#fff1f0' },
  normal:     { label: '일반',     icon: <UserOutlined />,        color: '#1890ff', bg: '#e6f7ff' },
  at_risk:    { label: '이탈위험', icon: <WarningOutlined />,     color: '#fa8c16', bg: '#fff7e6' },
  new_user:   { label: '신규',     icon: <UserAddOutlined />,     color: '#52c41a', bg: '#f6ffed' },
  dormant:    { label: '휴면',     icon: <PauseCircleOutlined />, color: '#8c8c8c', bg: '#fafafa' },
};

const API_BASE = '/api/v1';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState('ALL');
  const [platformSummary, setPlatformSummary] = useState(null);
  const [platformSummaryLoading, setPlatformSummaryLoading] = useState(false);

  useEffect(() => { fetchStats(); }, []);

  useEffect(() => {
    if (selectedPlatform !== 'ALL') {
      fetchPlatformSummary(selectedPlatform);
    } else {
      fetchPlatformSummary(null);
    }
  }, [selectedPlatform]);

  const fetchStats = async () => {
    try {
      const response = await dashboardApi.getStats();
      setStats(response.data);
    } catch (error) {
      // Try direct fetch as fallback
      try {
        const res = await fetch(`${API_BASE}/dashboard/stats`);
        if (res.ok) setStats(await res.json());
        else message.error('대시보드 통계 로딩 실패');
      } catch { message.error('대시보드 통계 로딩 실패'); }
    }
    finally { setLoading(false); }
  };

  const fetchPlatformSummary = async (platform) => {
    setPlatformSummaryLoading(true);
    try {
      const url = platform
        ? `${API_BASE}/insights/batch/summary?platform=${platform}`
        : `${API_BASE}/insights/batch/summary`;
      const res = await fetch(url);
      if (res.ok) setPlatformSummary(await res.json());
    } catch { /* ignore */ }
    setPlatformSummaryLoading(false);
  };

  const getStatusTag = (status) => {
    const colors = { draft: 'default', scheduled: 'blue', running: 'green', paused: 'orange', completed: 'purple' };
    const labels = { draft: '초안', scheduled: '예약됨', running: '진행중', paused: '일시정지', completed: '완료' };
    return <Tag color={colors[status]}>{labels[status]}</Tag>;
  };

  const columns = [
    { title: '캠페인명', dataIndex: 'name', ellipsis: true, render: (text, record) => <Link to={`/campaigns/${record.id}`}>{text}</Link> },
    { title: '상태', dataIndex: 'status', width: 90, render: (status) => getStatusTag(status) },
    { title: '발송', dataIndex: 'total_sent', width: 70, render: (count) => count?.toLocaleString() || 0 },
    { title: '오픈', dataIndex: 'total_opened', width: 70, render: (count) => count?.toLocaleString() || 0 },
  ];

  // ── 플랫폼별 연락처 수 계산 ──
  const getPlatformContactCount = () => {
    if (!stats?.platform_breakdown) return 0;
    if (selectedPlatform === 'ALL') return stats.total_contacts;
    return stats.platform_breakdown[selectedPlatform.toLowerCase()] || stats.platform_breakdown[selectedPlatform] || 0;
  };

  // ── 최근 캠페인 (플랫폼 필터 - 이름 기반 간이 필터) ──
  const getFilteredCampaigns = () => {
    if (!stats?.recent_campaigns) return [];
    if (selectedPlatform === 'ALL') return stats.recent_campaigns;
    // AI-Auto campaigns contain platform hints in name or we show all
    return stats.recent_campaigns;
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><Spin size="large" /></div>;

  const activePlatformConfig = PLATFORM_CONFIG[selectedPlatform];

  return (
    <div>
      {/* ── 헤더 + 플랫폼 선택 ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24 }}>대시보드</h1>
            <span style={{ color: '#888' }}>마케팅 성과를 한눈에 확인하세요</span>
          </div>
        </div>

        {/* 플랫폼 탭 */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {Object.entries(PLATFORM_CONFIG).map(([key, config]) => {
            const isSelected = selectedPlatform === key;
            const count = key === 'ALL'
              ? stats?.total_contacts || 0
              : stats?.platform_breakdown?.[key.toLowerCase()] || stats?.platform_breakdown?.[key] || 0;
            return (
              <div
                key={key}
                onClick={() => setSelectedPlatform(key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 16px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  border: isSelected ? `2px solid ${config.color}` : '1px solid #e8e8e8',
                  background: isSelected ? config.bg : '#fff',
                  transition: 'all 0.2s',
                  fontWeight: isSelected ? 600 : 400,
                  color: isSelected ? config.color : '#666',
                }}
              >
                <span style={{ fontSize: 16 }}>{config.emoji}</span>
                <span style={{ fontSize: 13 }}>{config.label}</span>
                <span style={{
                  background: isSelected ? config.color : '#d9d9d9',
                  color: '#fff',
                  borderRadius: 10,
                  padding: '1px 8px',
                  fontSize: 11,
                  fontWeight: 600,
                  minWidth: 24,
                  textAlign: 'center',
                }}>{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── 주요 통계 카드 ── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" style={{ borderTop: `3px solid ${activePlatformConfig.color}` }}>
            <Statistic
              title={<span style={{ fontSize: 12 }}>{selectedPlatform === 'ALL' ? '전체 연락처' : `${activePlatformConfig.label} 연락처`}</span>}
              value={getPlatformContactCount()}
              prefix={<TeamOutlined style={{ color: activePlatformConfig.color }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" style={{ borderTop: '3px solid #1890ff' }}>
            <Statistic
              title={<span style={{ fontSize: 12 }}>총 발송</span>}
              value={stats?.emails_sent_this_week || 0}
              prefix={<MailOutlined style={{ color: '#1890ff' }} />}
              suffix={<span style={{ fontSize: 12, color: '#999' }}>이번 주</span>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" style={{ borderTop: '3px solid #52c41a' }}>
            <Statistic
              title={<span style={{ fontSize: 12 }}>평균 오픈률</span>}
              value={stats?.average_open_rate || 0}
              precision={1}
              prefix={<EyeOutlined style={{ color: '#52c41a' }} />}
              suffix="%"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card size="small" style={{ borderTop: '3px solid #fa8c16' }}>
            <Statistic
              title={<span style={{ fontSize: 12 }}>평균 클릭률</span>}
              value={stats?.average_click_rate || 0}
              precision={1}
              prefix={<CheckCircleOutlined style={{ color: '#fa8c16' }} />}
              suffix="%"
            />
          </Card>
        </Col>
      </Row>

      {/* ── 사용자 유형 분포 (플랫폼별) ── */}
      {platformSummary && platformSummary.total_analyzed > 0 && (
        <Card
          size="small"
          title={
            <span style={{ fontSize: 14 }}>
              {activePlatformConfig.emoji} {selectedPlatform === 'ALL' ? '전체' : activePlatformConfig.label} 사용자 유형 분포
              <span style={{ color: '#999', fontWeight: 400, marginLeft: 8, fontSize: 12 }}>
                AI 분석 기준 · {platformSummary.total_analyzed}명
              </span>
            </span>
          }
          style={{ marginBottom: 24, borderRadius: 10 }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
            {Object.entries(USER_TYPE_CONFIG).map(([key, config]) => {
              const data = platformSummary.user_type_distribution?.[key];
              const count = data?.count || 0;
              const pct = platformSummary.total_analyzed > 0
                ? ((count / platformSummary.total_analyzed) * 100).toFixed(0)
                : 0;
              return (
                <div key={key} style={{
                  background: config.bg,
                  borderRadius: 10,
                  padding: '14px 12px',
                  textAlign: 'center',
                  border: `1px solid ${config.color}20`,
                }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: config.color, lineHeight: 1 }}>{count}</div>
                  <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>{config.label}</div>
                  <Progress
                    percent={Number(pct)} showInfo={false} size="small"
                    strokeColor={config.color}
                    style={{ marginTop: 6 }}
                  />
                  <div style={{ fontSize: 10, color: '#999' }}>{pct}%</div>
                  {data?.avg_score && (
                    <div style={{ fontSize: 10, color: config.color, marginTop: 2 }}>평균 {data.avg_score}점</div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Row gutter={[16, 16]}>
        {/* ── 최근 캠페인 ── */}
        <Col xs={24} lg={14}>
          <Card
            size="small"
            title={<span style={{ fontSize: 14 }}>최근 캠페인</span>}
            extra={<Link to="/campaigns" style={{ fontSize: 12 }}>전체 보기</Link>}
            style={{ borderRadius: 10 }}
          >
            <Table
              columns={columns}
              dataSource={getFilteredCampaigns()}
              rowKey="id"
              pagination={false}
              size="small"
              locale={{ emptyText: <Empty description="캠페인 없음" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            />
          </Card>
        </Col>

        {/* ── 오른쪽 사이드바 ── */}
        <Col xs={24} lg={10}>
          {/* 플랫폼별 연락처 분포 */}
          <Card
            size="small"
            title={<span style={{ fontSize: 14 }}>📊 플랫폼별 연락처</span>}
            style={{ borderRadius: 10, marginBottom: 16 }}
          >
            {stats?.platform_breakdown && Object.entries(stats.platform_breakdown).length > 0 ? (
              <div>
                {Object.entries(stats.platform_breakdown)
                  .sort((a, b) => b[1] - a[1])
                  .map(([platform, count]) => {
                    const key = platform.toUpperCase();
                    const config = PLATFORM_CONFIG[key] || { label: platform, color: '#8c8c8c', emoji: '📋' };
                    const total = Object.values(stats.platform_breakdown).reduce((a, b) => a + b, 0);
                    const pct = total > 0 ? ((count / total) * 100).toFixed(0) : 0;
                    return (
                      <div
                        key={platform}
                        onClick={() => setSelectedPlatform(key)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '10px 12px', marginBottom: 6,
                          borderRadius: 8,
                          background: selectedPlatform === key ? config.bg : '#fafafa',
                          border: selectedPlatform === key ? `1px solid ${config.color}40` : '1px solid transparent',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                      >
                        <span style={{ fontSize: 18, width: 28 }}>{config.emoji}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                            <span style={{ fontWeight: 500, fontSize: 13, color: config.color }}>{config.label}</span>
                            <span style={{ fontWeight: 600, fontSize: 13 }}>{count}명</span>
                          </div>
                          <Progress
                            percent={Number(pct)} showInfo={false} size="small"
                            strokeColor={config.color}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <Empty description="데이터 없음" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>

          {/* 동기화 상태 */}
          <Card
            size="small"
            title={<span style={{ fontSize: 14 }}><SyncOutlined style={{ marginRight: 6 }} />동기화 상태</span>}
            extra={<Link to="/sync" style={{ fontSize: 12 }}>설정</Link>}
            style={{ borderRadius: 10 }}
          >
            {stats?.sync_status?.map(sync => {
              const key = (sync.platform || '').toUpperCase();
              const config = PLATFORM_CONFIG[key] || { label: sync.platform, color: '#8c8c8c' };
              const statusColor = sync.last_sync_status === 'completed' ? '#52c41a'
                : sync.last_sync_status === 'running' ? '#1890ff'
                : sync.last_sync_status === 'failed' ? '#ff4d4f' : '#d9d9d9';
              const statusLabel = sync.last_sync_status === 'completed' ? '완료'
                : sync.last_sync_status === 'running' ? '실행중'
                : sync.last_sync_status === 'failed' ? '실패' : '대기';
              return (
                <div key={sync.platform} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 0', borderBottom: '1px solid #f5f5f5',
                }}>
                  <span style={{ fontWeight: 500, color: config.color, fontSize: 13 }}>{config.label}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {sync.last_synced_at && (
                      <span style={{ fontSize: 11, color: '#999' }}>
                        {new Date(sync.last_synced_at).toLocaleDateString('ko-KR')}
                      </span>
                    )}
                    <Badge color={statusColor} text={<span style={{ fontSize: 12 }}>{statusLabel}</span>} />
                  </div>
                </div>
              );
            })}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
